// Project outliner — hierarchical tree of the project's structure.
//
// Mounted in the left rail of ProjectEditorPage below CabinetPalette.
// Shape: Garniturs (collapsible) → Cabinets. Cabinets are selectable;
// click syncs with the 3D scene's selectedIds state. Shift-click adds
// to the selection.
//
// Drag-and-drop: drag a cabinet row onto a different garnitur header
// to re-parent it (calls `onMoveCabinet`). Same garnitur or same row
// is a no-op.
//
// Expand state persists in localStorage keyed by project id, so a user
// who collapses "Lower run" sees it collapsed on next reload.

import { useEffect, useMemo, useState, type DragEvent, type KeyboardEvent, type MouseEvent } from "react";
import type { Cabinet, CabinetPart, Garnitur, GarniturType, Id } from "@/lib/types";
import { getCabinetClass } from "@/lib/cabinets";
import { partsFor, type CatalogIndex } from "@/lib/cabinets/bom";

const TYPE_LABEL: Record<GarniturType, string> = {
  upper: "Upper", lower: "Lower", tall: "Tall", island: "Island", freestanding: "Free",
};
const TYPE_COLOR: Record<GarniturType, string> = {
  upper: "#7aa6d6", lower: "#d89c5a", tall: "#9a78c8", island: "#6ec48b", freestanding: "#9aa3ad",
};

interface Props {
  projectId: Id;
  garniturs: Garnitur[];
  /** Set of selected cabinet ids — shared state from ProjectEditorPage so
   *  selection in the tree mirrors selection in the 3D scene. */
  selectedIds: Set<Id>;
  /** Cabinet selection. `shiftHeld` adds/removes from the multiselect. */
  onSelectCabinet: (id: Id | null, shiftHeld?: boolean) => void;
  /** Re-parent a cabinet to a different garnitur via drag-and-drop. */
  onMoveCabinet: (cabinetId: Id, targetGarniturId: Id) => void;
  /** Catalog index — used to compute the parts list under each expanded
   *  cabinet via `partsFor()`. When absent, cabinet rows can't be
   *  expanded. */
  catalog?: CatalogIndex;
  /** Click on a part row — pops the part inspector in the right rail. */
  onSelectPart?: (cabinetId: Id, partId: string) => void;
  /** Click on a drawer row — pops the drawer inspector in the right rail. */
  onSelectDrawer?: (cabinetId: Id, drawerId: Id) => void;
  /** Currently-inspected secondary target (mirrors ProjectEditorPage)
   *  so the matching drawer or part row paints with the same accent
   *  the selected cabinet uses. */
  inspectorTarget?:
    | { kind: "drawer"; cabinetId: Id; drawerId: Id }
    | { kind: "part";   cabinetId: Id; partId: string }
    | null;
}

// ── Persisted expand state ─────────────────────────────────────

function loadExpanded(projectId: Id, garniturs: Garnitur[]): Set<Id> {
  const key = `outliner.${projectId}.expanded`;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const ids = JSON.parse(raw) as Id[];
      return new Set(ids);
    }
  } catch { /* fall through */ }
  // First visit: expand everything so the user sees the full tree.
  return new Set(garniturs.map((g) => g._id));
}

function persistExpanded(projectId: Id, ids: Set<Id>) {
  const key = `outliner.${projectId}.expanded`;
  localStorage.setItem(key, JSON.stringify([...ids]));
}

// ── Outliner ───────────────────────────────────────────────────

export function ProjectOutliner({
  projectId,
  garniturs,
  selectedIds,
  onSelectCabinet,
  onMoveCabinet,
  catalog,
  onSelectPart,
  onSelectDrawer,
  inspectorTarget = null,
}: Props) {
  const [expanded, setExpanded] = useState<Set<Id>>(() => loadExpanded(projectId, garniturs));
  const [expandedCabinets, setExpandedCabinets] = useState<Set<Id>>(new Set());
  const [dropTarget, setDropTarget] = useState<Id | null>(null);

  // Auto-expand garniturs that contain a currently-selected cabinet —
  // otherwise picking from the 3D scene could put the highlighted row
  // out of view inside a collapsed branch. We don't mutate localStorage
  // here: a user who deliberately collapsed a garnitur will see it pop
  // open while their selection is inside it, but their collapsed state
  // is restored once they click elsewhere. Newly-added garniturs land
  // expanded via loadExpanded's empty-storage fallback.
  useEffect(() => {
    if (selectedIds.size === 0) return;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const g of garniturs) {
        if (next.has(g._id)) continue;
        if (g.cabinets?.some((c) => selectedIds.has(c._id))) {
          next.add(g._id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [garniturs, selectedIds]);

  function toggle(garniturId: Id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(garniturId)) next.delete(garniturId);
      else next.add(garniturId);
      persistExpanded(projectId, next);
      return next;
    });
  }

  function toggleCabinet(cabinetId: Id) {
    setExpandedCabinets((prev) => {
      const next = new Set(prev);
      if (next.has(cabinetId)) next.delete(cabinetId);
      else next.add(cabinetId);
      return next;
    });
  }

  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        padding: "12px 8px",
        overflowY: "auto",
        flex: 1,
        minHeight: 0,
      }}
    >
      <div className="label" style={{ padding: "0 8px 8px" }}>OUTLINER</div>
      {garniturs.length === 0 ? (
        <div className="muted" style={{ fontSize: 11, padding: "0 8px" }}>
          Add a cabinet from the palette to start the tree.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {garniturs.map((g) => (
            <GarniturNode
              key={g._id}
              garnitur={g}
              expanded={expanded.has(g._id)}
              dropActive={dropTarget === g._id}
              selectedIds={selectedIds}
              expandedCabinets={expandedCabinets}
              onToggle={() => toggle(g._id)}
              onToggleCabinet={toggleCabinet}
              onSelectCabinet={onSelectCabinet}
              catalog={catalog}
              onSelectPart={onSelectPart}
              onSelectDrawer={onSelectDrawer}
              inspectorTarget={inspectorTarget}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDropTarget(g._id);
              }}
              onDragLeave={() => setDropTarget((d) => (d === g._id ? null : d))}
              onDrop={(e) => {
                e.preventDefault();
                setDropTarget(null);
                const raw = e.dataTransfer.getData("application/x-cabinet-id");
                const cabinetId = Number(raw);
                if (!Number.isFinite(cabinetId)) return;
                // No-op if the cabinet is already in this garnitur.
                if (g.cabinets.some((c) => c._id === cabinetId)) return;
                onMoveCabinet(cabinetId, g._id);
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Garnitur row + nested cabinets ─────────────────────────────

interface GarniturNodeProps {
  garnitur: Garnitur;
  expanded: boolean;
  dropActive: boolean;
  selectedIds: Set<Id>;
  expandedCabinets: Set<Id>;
  onToggle: () => void;
  onToggleCabinet: (id: Id) => void;
  onSelectCabinet: (id: Id | null, shiftHeld?: boolean) => void;
  catalog?: CatalogIndex;
  onSelectPart?: (cabinetId: Id, partId: string) => void;
  onSelectDrawer?: (cabinetId: Id, drawerId: Id) => void;
  inspectorTarget?: Props["inspectorTarget"];
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
}

function GarniturNode({
  garnitur, expanded, dropActive, selectedIds, expandedCabinets,
  onToggle, onToggleCabinet, onSelectCabinet, catalog, onSelectPart, onSelectDrawer,
  inspectorTarget,
  onDragOver, onDragLeave, onDrop,
}: GarniturNodeProps) {
  const color = garnitur.color ?? TYPE_COLOR[garnitur.type];
  const count = garnitur.cabinets?.length ?? 0;

  return (
    <li>
      <div
        role="treeitem"
        aria-expanded={expanded}
        onClick={onToggle}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "4px 6px",
          fontSize: 12,
          cursor: "pointer",
          borderRadius: 4,
          background: dropActive ? "rgba(255,170,85,0.18)" : "transparent",
          border: `1px solid ${dropActive ? "var(--accent, #ffaa55)" : "transparent"}`,
          userSelect: "none",
        }}
        title={`${TYPE_LABEL[garnitur.type]} run · ${count} cabinet${count === 1 ? "" : "s"}`}
      >
        <span style={{ width: 12, textAlign: "center", color: "var(--fg-dim)" }}>
          {count === 0 ? "·" : expanded ? "▾" : "▸"}
        </span>
        <span
          aria-hidden
          style={{ width: 9, height: 9, borderRadius: 5, background: color, flexShrink: 0 }}
        />
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {garnitur.name}
        </span>
        <span className="muted" style={{ fontSize: 10, minWidth: 14, textAlign: "right" }}>
          {count}
        </span>
      </div>

      {expanded && count > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {garnitur.cabinets.map((c) => (
            <CabinetRow
              key={c._id}
              cabinet={c}
              garniturColor={color}
              selected={selectedIds.has(c._id)}
              partsExpanded={expandedCabinets.has(c._id)}
              catalog={catalog}
              onClick={(e) => onSelectCabinet(c._id, e.shiftKey)}
              onTogglePartsExpand={() => onToggleCabinet(c._id)}
              onSelectPart={onSelectPart}
              onSelectDrawer={onSelectDrawer}
              inspectorTarget={inspectorTarget}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Cabinet row ────────────────────────────────────────────────

interface CabinetRowProps {
  cabinet: Cabinet;
  garniturColor: string;
  selected: boolean;
  partsExpanded: boolean;
  catalog?: CatalogIndex;
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
  onTogglePartsExpand: () => void;
  onSelectPart?: (cabinetId: Id, partId: string) => void;
  onSelectDrawer?: (cabinetId: Id, drawerId: Id) => void;
  inspectorTarget?: Props["inspectorTarget"];
}

function CabinetRow({
  cabinet, garniturColor, selected, partsExpanded, catalog,
  onClick, onTogglePartsExpand, onSelectPart, onSelectDrawer,
  inspectorTarget,
}: CabinetRowProps) {
  const cls = getCabinetClass(cabinet.classId);
  const label = cls?.label ?? cabinet.classId;
  const canExpand = !!catalog && !!onSelectPart;

  // Parts list computed only when this row is actually expanded —
  // partsFor() touches the DSL evaluator and is wasteful to run for
  // every cabinet in the tree on every render.
  const parts = useMemo<CabinetPart[]>(
    () => (partsExpanded && catalog ? partsFor(cabinet, catalog) : []),
    [partsExpanded, catalog, cabinet],
  );

  return (
    <li>
      <div
        role="treeitem"
        tabIndex={0}
        draggable
        aria-selected={selected}
        aria-expanded={canExpand ? partsExpanded : undefined}
        onClick={onClick}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") onClick(e as unknown as MouseEvent<HTMLDivElement>);
        }}
        onDragStart={(e: DragEvent<HTMLDivElement>) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("application/x-cabinet-id", String(cabinet._id));
        }}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "3px 6px 3px 18px",       // align past garnitur chevron
          fontSize: 12,
          cursor: "pointer",
          borderRadius: 4,
          background: selected ? "rgba(255,170,85,0.10)" : "transparent",
          border: `1px solid ${selected ? "var(--accent, #ffaa55)" : "transparent"}`,
          userSelect: "none",
        }}
        title={`${label} #${cabinet._id} · ${cabinet.width}″ × ${cabinet.height}″ × ${cabinet.depth}″`}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePartsExpand(); }}
          disabled={!canExpand}
          aria-label={partsExpanded ? "Collapse parts" : "Expand parts"}
          title={canExpand ? (partsExpanded ? "Collapse parts" : "Expand parts") : "Catalog loading…"}
          style={{
            background: "transparent", border: "none", padding: 0,
            width: 12, textAlign: "center", color: "var(--fg-dim)",
            cursor: canExpand ? "pointer" : "default",
            fontSize: 11, lineHeight: 1,
          }}
        >
          {canExpand ? (partsExpanded ? "▾" : "▸") : "·"}
        </button>
        <span
          aria-hidden
          style={{
            width: 6, height: 6, borderRadius: 3,
            background: garniturColor,
            opacity: 0.6,
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <span className="muted" style={{ fontSize: 10 }}>#{cabinet._id}</span>
      </div>

      {partsExpanded && parts.length > 0 && (() => {
        // Drawer parts share the id prefix `drawer_<id>_…`; group them
        // under their drawer so the tree reads `cabinet → drawer →
        // parts` instead of dumping every drawer panel flat at the
        // cabinet level. Class parts (no prefix) render afterwards.
        const drawerGroups = new Map<number, CabinetPart[]>();
        const classParts: CabinetPart[] = [];
        for (const p of parts) {
          const m = /^drawer_(\d+)_/.exec(p.id);
          if (m) {
            const did = Number(m[1]);
            const arr = drawerGroups.get(did) ?? [];
            arr.push(p);
            drawerGroups.set(did, arr);
          } else {
            classParts.push(p);
          }
        }
        return (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {(cabinet.drawers ?? []).map((d, idx) => (
              <DrawerNode
                key={d._id}
                drawer={d}
                index={idx}
                cabinetId={cabinet._id}
                parts={drawerGroups.get(d._id) ?? []}
                selected={
                  inspectorTarget?.kind === "drawer"
                    && inspectorTarget.cabinetId === cabinet._id
                    && inspectorTarget.drawerId === d._id
                }
                inspectorTarget={inspectorTarget}
                onSelectPart={onSelectPart}
                onSelectDrawer={onSelectDrawer}
              />
            ))}
            {classParts.map((p) => (
              <PartRow
                key={p.id}
                part={p}
                selected={
                  inspectorTarget?.kind === "part"
                    && inspectorTarget.cabinetId === cabinet._id
                    && inspectorTarget.partId === p.id
                }
                onClick={() => onSelectPart?.(cabinet._id, p.id)}
              />
            ))}
          </ul>
        );
      })()}
    </li>
  );
}

// ── Drawer node ────────────────────────────────────────────────

function DrawerNode({
  drawer, index, cabinetId, parts, selected, inspectorTarget,
  onSelectPart, onSelectDrawer,
}: {
  drawer: import("@/lib/types").Drawer;
  index: number;
  cabinetId: Id;
  parts: CabinetPart[];
  selected: boolean;
  inspectorTarget?: Props["inspectorTarget"];
  onSelectPart?: (cabinetId: Id, partId: string) => void;
  onSelectDrawer?: (cabinetId: Id, drawerId: Id) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = drawer.label ?? `Drawer ${index + 1}`;
  return (
    <li>
      <div
        onClick={(e) => {
          // Chevron toggles expansion; label area selects the drawer.
          if ((e.target as HTMLElement).dataset?.chevron === "1") {
            setOpen((o) => !o);
          } else {
            onSelectDrawer?.(cabinetId, drawer._id);
          }
        }}
        aria-selected={selected}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "3px 6px 3px 30px",   // indented under cabinet
          fontSize: 11,
          cursor: "pointer",
          userSelect: "none",
          borderRadius: 4,
          background: selected ? "rgba(255,170,85,0.10)" : "transparent",
          border: `1px solid ${selected ? "var(--accent, #ffaa55)" : "transparent"}`,
        }}
        title={`${label} · ${drawer.height.toFixed(2)}″ tall · ${drawer.slideDepthMm}mm slides`}
      >
        <span
          data-chevron="1"
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
          style={{ width: 12, textAlign: "center", color: "var(--fg-dim)", cursor: "pointer" }}
        >
          {parts.length === 0 ? "·" : open ? "▾" : "▸"}
        </span>
        <span aria-hidden style={{ width: 5, height: 5, borderRadius: 3, background: "#5e3b1f", flexShrink: 0 }} />
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span className="muted" style={{ fontSize: 9 }}>{drawer.slideDepthMm}mm</span>
      </div>
      {open && parts.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {parts.map((p) => (
            <PartRow
              key={p.id}
              part={p}
              selected={
                inspectorTarget?.kind === "part"
                  && inspectorTarget.cabinetId === cabinetId
                  && inspectorTarget.partId === p.id
              }
              onClick={() => onSelectPart?.(cabinetId, p.id)}
              indent={56}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ── Part row ───────────────────────────────────────────────────

function PartRow({
  part, onClick, indent = 44, selected = false,
}: {
  part: CabinetPart;
  onClick: () => void;
  /** Left-padding of the row (px). Default = 44 puts a part flush
   *  under a cabinet row; drawer-nested parts use 56 to indent past
   *  the drawer node. */
  indent?: number;
  /** Highlight when this part matches the right-rail inspector target. */
  selected?: boolean;
}) {
  return (
    <li>
      <div
        role="treeitem"
        tabIndex={0}
        aria-selected={selected}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: `2px 6px 2px ${indent}px`,
          fontSize: 11,
          cursor: "pointer",
          borderRadius: 4,
          userSelect: "none",
          background: selected ? "rgba(255,170,85,0.10)" : "transparent",
          border: `1px solid ${selected ? "var(--accent, #ffaa55)" : "transparent"}`,
        }}
        title={`${part.name} · ${part.w.toFixed(1)}″ × ${part.h.toFixed(1)}″`}
      >
        <span
          aria-hidden
          style={{
            width: 4, height: 4, borderRadius: 2,
            background: "var(--fg-dim)",
            opacity: 0.4,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {part.id}
        </span>
        <span className="muted" style={{ fontSize: 9 }}>{part.role ?? ""}</span>
      </div>
    </li>
  );
}
